"use client";

import { Card, Flex, Grid, Text } from "@radix-ui/themes";
import { Button, Skeleton } from "./elements.js";
import { IconPanel } from "./icon-panel.js";
import { ButtonIcon, LockClosedIcon } from "@radix-ui/react-icons";
import { SetPasswordDialog } from "./set-password-dialog.js";
import { ChangePasswordDialog } from "./change-password-dialog.js";
import { AddMfaDialog } from "./add-mfa-dialog.js";
import type {
  AuthenticationInformationResponseData as AuthenticationSettings,
  AuthenticationInformationResponseDataVerificationMethodsPassword as PasswordVerificationMethod,
  AuthenticationInformationResponseDataVerificationMethodsMfa as MfaVerificationMethod,
} from "../api/endpoint.js";
import { ResetMfaDialog } from "./reset-mfa-dialog.js";
import * as CardList from "./card-list.js";
import { GenericError } from "./generic-error.js";
import {
  getComparativeReadableDate,
  getDomProps,
  type WidgetRootDomProps,
  type WidgetRootState,
} from "./utils.js";
import { Translation } from "./i18n/translation.js";
import { useLocale } from "./i18n/use-locale.js";

interface UserSecurityProps extends WidgetRootDomProps {
  settings: AuthenticationSettings;
}

const UserSecurity: React.FC<UserSecurityProps> = ({
  settings,
  ...domProps
}) => {
  const passwordSettings = settings.verificationMethods.Password;
  const mfaSettings = settings.verificationMethods.Mfa;
  return (
    <CardList.Root {...getWidgetRootDomProps("resolved", domProps)}>
      {passwordSettings && (
        <CardList.Item>
          <PasswordSettings settings={passwordSettings} />
        </CardList.Item>
      )}
      {mfaSettings && (
        <CardList.Item>
          <MfaSettings
            settings={mfaSettings}
            isPasswordSet={!!passwordSettings?.isSetUp}
          />
        </CardList.Item>
      )}
    </CardList.Root>
  );
};

interface UserSecurityErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const UserSecurityError: React.FC<UserSecurityErrorProps> = ({
  error,
  ...domProps
}) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <GenericError error={error} />
    </Card>
  );
};

interface UserSecurityLoadingProps extends WidgetRootDomProps {}

const UserSecurityLoading: React.FC<UserSecurityLoadingProps> = (props) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", props)}>
      <Grid columns="auto 1fr auto" align="center" gap="4">
        <Skeleton>
          <IconPanel />
        </Skeleton>

        <Flex direction="column">
          <Text size="2" highContrast weight="bold" as="p" mb="-2px">
            <Skeleton>
              <Translation
                defaultMessage="Password"
                id="z19aZa"
                description="Label for password security settings section"
              />
            </Skeleton>
          </Text>
          <Text as="p" size="2" color="gray">
            <Skeleton>
              <Translation
                defaultMessage="Set a password to access your account"
                id="leodiT"
                description="Description for setting up a password"
              />
            </Skeleton>
          </Text>
        </Flex>

        <ChangePasswordDialog>
          <Skeleton>
            <Button variant="secondary">
              <Translation
                defaultMessage="Change"
                id="/ZV6cj"
                description="Button label to change password"
              />
            </Button>
          </Skeleton>
        </ChangePasswordDialog>
      </Grid>
    </Card>
  );
};

function PasswordSettings({
  settings,
}: {
  settings: NonNullable<PasswordVerificationMethod>;
}) {
  const locale = useLocale();

  return (
    <Grid columns="auto 1fr auto" align="center" gap="4">
      <IconPanel>
        <LockClosedIcon />
      </IconPanel>

      <Flex direction="column">
        <Text size="2" highContrast weight="bold" as="p" mb="-2px">
          <Translation
            defaultMessage="Password"
            id="z19aZa"
            description="Label for password security settings section"
          />
        </Text>

        {settings.isSetUp ? (
          settings.lastUsed && (
            <Text size="2" color="gray">
              <Translation
                defaultMessage="Last used {time}"
                id="Jqn+r+"
                description="Label indicating when password was last used with time"
                values={{
                  time: getComparativeReadableDate(
                    new Date(),
                    new Date(settings.lastUsed),
                    { locale },
                  ),
                }}
              />
            </Text>
          )
        ) : (
          <Text as="p" size="2" color="gray">
            <Translation
              defaultMessage="Set a password to access your account"
              id="leodiT"
              description="Description for setting up a password"
            />
          </Text>
        )}
      </Flex>

      {settings.isSetUp ? (
        <ChangePasswordDialog>
          <Button variant="secondary">
            <Translation
              defaultMessage="Change"
              id="/ZV6cj"
              description="Button label to change password"
            />
          </Button>
        </ChangePasswordDialog>
      ) : (
        <SetPasswordDialog>
          <Button variant="secondary">
            <Translation
              defaultMessage="Set a password"
              id="zokPvK"
              description="Button label to set a new password"
            />
          </Button>
        </SetPasswordDialog>
      )}
    </Grid>
  );
}

function MfaSettings({
  settings,
  isPasswordSet,
}: {
  settings: NonNullable<MfaVerificationMethod>;
  isPasswordSet: boolean;
}) {
  const locale = useLocale();

  return (
    <Grid columns="auto 1fr auto" align="center" gap="4">
      <IconPanel>
        <ButtonIcon />
      </IconPanel>

      <Flex direction="column">
        <Text size="2" highContrast weight="bold" as="p" mb="-2px">
          <Translation
            defaultMessage="Multi-factor authentication"
            id="f3uhhm"
            description="Label for multi-factor authentication settings section"
          />
        </Text>

        {settings.isSetUp ? (
          <Text size="2">
            <Translation
              defaultMessage="Authenticator app"
              id="0p/a72"
              description="Label indicating MFA is set up with an authenticator app"
            />
            {settings.lastUsed && (
              <>
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                <Text size="2" color="gray" mx="1">
                  ∙
                </Text>
                <Text size="2" color="gray">
                  <Translation
                    defaultMessage="Last used {time}"
                    id="h3R6gY"
                    description="Label indicating when MFA was last used with time"
                    values={{
                      time: getComparativeReadableDate(
                        new Date(),
                        new Date(settings.lastUsed),
                        { locale },
                      ),
                    }}
                  />
                </Text>
              </>
            )}
          </Text>
        ) : (
          <Text as="p" size="2" color="gray">
            <Translation
              defaultMessage="Secure your account with an extra verification step"
              id="xZFdkq"
              description="Description for setting up multi-factor authentication"
            />
          </Text>
        )}
      </Flex>

      {settings.isSetUp ? (
        <ResetMfaDialog isPasswordSet={isPasswordSet}>
          <Button variant="secondary">
            <Translation
              defaultMessage="Disable"
              id="4flMgv"
              description="Button label to disable multi-factor authentication"
            />
          </Button>
        </ResetMfaDialog>
      ) : (
        <AddMfaDialog>
          <Button variant="secondary">
            <Translation
              defaultMessage="Set up authenticator app"
              id="oLNIQv"
              description="Button label to set up multi-factor authentication with an authenticator app"
            />
          </Button>
        </AddMfaDialog>
      )}
    </Grid>
  );
}

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "user-security",
    widgetState: state,
  });
}

export type {
  UserSecurityProps,
  UserSecurityLoadingProps,
  UserSecurityErrorProps,
};
export { UserSecurity, UserSecurityLoading, UserSecurityError };
